import CustomBreadcrumb from '@/components/custom-breadcrumb/custom-breadcrumb';
import { MaintenancesHeader } from '@/components/maintenances-header/maintenances-header';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs/use-breadcrumbs';
import { Flex } from 'antd';
import styles from './maintenances.module.scss';
import { MaintenanceTable } from '@/components/maintenances-table/maintenances-table';
import { useEffect, useState } from 'react';
import { useUser } from '@/hooks/use-user/use-user';
import { useMaintenances } from '@/hooks/use-maintenances/use-maintenances';
import {
  MaintenancePriority,
  MaintenancesFilters,
  MaintenanceStatus,
} from '@/services/maintenances-service/maintenances-service.types';
import {
  DEFAULT_PAGINATION,
  MAX_PAGE_SIZE,
  useSortAndPagination,
} from '@/hooks/use-sort-and-pagination/use-sort-and-pagination';
import { OrderBy } from '@/hooks/use-sort-and-pagination/use-sort-and-pagination.types';
import { WorkOrderDetailsDrawer } from '@/components/work-order-details-drawer/work-order-details-drawer';
import { useMaintenance } from '@/hooks/use-maintenance/use-maintenance';
import { calculateDateFromISOtoYYYY_MM_DD } from '@/utils/helpers';
import { useCompanyStore, useTimeRangeStore } from '@/store/app.store';
import { DEFAULT_TIME_RANGE } from '@/store/initial-time-range';
import dayjs, { Dayjs } from 'dayjs';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { readUrlCount, readUrlString, resolveInitialTimeRange } from '@/utils/url-state/url-state';
import { useUrlSync } from '@/hooks/use-url-sync/use-url-sync';
import { useCompanyAccessGuard } from '@/hooks/use-company-access-guard/use-company-access-guard';
import { useInitialSearchParams } from '@/hooks/use-initial-search-params/use-initial-search-params';
import { useUrlCompanyToStoreSync } from '@/hooks/use-url-company-sync/use-url-company-sync';

export function Maintenances() {
  const { companyName, setCompanyName } = useCompanyStore();
  const { timeRange, startTime, endTime, setTimeRangeFilter } = useTimeRangeStore();
  const [isMaintenanceDrawerVisible, setMaintenanceDrawerVisible] = useState(false);
  const items = useBreadcrumbs();

  const initialUrl = useInitialSearchParams();
  useUrlCompanyToStoreSync(initialUrl);

  const initialTimeFilter = resolveInitialTimeRange(
    initialUrl,
    timeRange,
    startTime,
    endTime,
    DEFAULT_TIME_RANGE
  );

  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    initialTimeFilter.start_time && initialTimeFilter.end_time
      ? [dayjs(initialTimeFilter.start_time), dayjs(initialTimeFilter.end_time)]
      : null
  );
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { companies } = useCompaniesList();

  const { userData } = useUser();
  const isCompanyAdmin = userData?.roles.find((role) => role.name === 'company_admin');
  const isAdmin = isCompanyAdmin && !!userData?.default_company;

  const [maintenancesFilters, setMaintenancesFilters] = useState<MaintenancesFilters>({
    time_range: initialTimeFilter.time_range as MaintenancesFilters['time_range'],
    company_name:
      readUrlString(initialUrl, 'company_name') ??
      companyName ??
      (isCompanyAdmin ? userData?.default_company : userData?.company?.name),
    start_time: initialTimeFilter.start_time,
    end_time: initialTimeFilter.end_time,
    status: readUrlString(initialUrl, 'status'),
    priority: readUrlString(initialUrl, 'priority'),
    created_by: readUrlString(initialUrl, 'created_by'),
    query: readUrlString(initialUrl, 'query'),
  });

  useEffect(() => {
    if (readUrlString(initialUrl, 'company_name')) return;
    if (companyName) {
      setMaintenancesFilters((prev) => ({
        ...prev,
        company_name: companyName,
      }));
    } else if (isCompanyAdmin && userData?.default_company) {
      setMaintenancesFilters((prev) => ({
        ...prev,
        company_name: userData.default_company,
      }));
    }
  }, [companyName, isCompanyAdmin, userData?.default_company]);

  const { paginationDetails, sortDetails, onPaginationChanged, onSortChange } =
    useSortAndPagination({
      defaultPagination: {
        page: readUrlCount(initialUrl, 'page') ?? DEFAULT_PAGINATION.page,
        items_per_page:
          readUrlCount(initialUrl, 'items_per_page', MAX_PAGE_SIZE) ??
          DEFAULT_PAGINATION.items_per_page,
      },
      defaultSorting: {
        sort_by: readUrlString(initialUrl, 'sort_by') ?? 'created_at',
        order_by: (readUrlString(initialUrl, 'order_by') ?? 'desc') as OrderBy,
      },
    });

  useCompanyAccessGuard(maintenancesFilters.company_name, companies, (allowedName) => {
    setMaintenancesFilters((prev) => ({ ...prev, company_name: allowedName }));
    setCompanyName(allowedName);
  });

  useUrlSync(
    {
      time_range: maintenancesFilters.time_range,
      start_time: maintenancesFilters.start_time,
      end_time: maintenancesFilters.end_time,
      company_name: maintenancesFilters.company_name,
      status: maintenancesFilters.status,
      priority: maintenancesFilters.priority,
      created_by: maintenancesFilters.created_by,
      query: maintenancesFilters.query,
      page: paginationDetails.page,
      items_per_page: paginationDetails.items_per_page,
      sort_by: sortDetails?.sort_by,
      order_by: sortDetails?.order_by,
    },
    {
      defaults: {
        time_range: DEFAULT_TIME_RANGE,
        page: DEFAULT_PAGINATION.page,
        items_per_page: DEFAULT_PAGINATION.items_per_page,
        sort_by: 'created_at',
        order_by: 'desc',
      },
    }
  );

  const handleChange = (page?: number, perPage?: number, sortBy?: string, orderBy?: OrderBy) => {
    onPaginationChanged(
      page || DEFAULT_PAGINATION.page,
      perPage || DEFAULT_PAGINATION.items_per_page
    );
    onSortChange(sortBy || 'created_at', orderBy || 'desc');
  };

  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState<number | null>(null);

  const handleShowMaintenanceDetails = (id: number) => {
    setMaintenanceDrawerVisible(true);
    setSelectedMaintenanceId(id);
  };

  const {
    maintenancesData,
    refetch: refetchMaintenancesData,
    isPending,
  } = useMaintenances({
    filtersParams: {
      ...maintenancesFilters,
      company_name: maintenancesFilters.company_name,
      time_zone: timeZone,
    },
    paginationParams: { ...paginationDetails, ...sortDetails },
  });

  const { maintenanceData } = useMaintenance(selectedMaintenanceId || 0);

  const handleRefreshData = () => {
    refetchMaintenancesData();
  };

  const closeDrawer = () => {
    setMaintenanceDrawerVisible(false);
  };

  const filteredData = {
    id: maintenanceData?.id || 0,
    phone: maintenanceData?.phone || '',
    company_name: maintenanceData?.company_name || '',
    has_property: maintenanceData?.has_property || false,
    priority: maintenanceData?.priority
      ? (maintenanceData.priority.toLowerCase() as MaintenancePriority)
      : 'critical',
    created_at: maintenanceData?.created_at
      ? calculateDateFromISOtoYYYY_MM_DD(maintenanceData.created_at)
      : '',
    updated_at: maintenanceData?.updated_at
      ? calculateDateFromISOtoYYYY_MM_DD(maintenanceData.updated_at)
      : '',
    status: (maintenanceData?.status.toLowerCase() as MaintenanceStatus) || 'close',
    description: maintenanceData?.description || '',
    yardi_id: maintenanceData?.yardi_id || '',
    entry_notes: maintenanceData?.entry_notes || '-',
  };

  const handleSubmit = (status: MaintenanceStatus, description: string) => {
    console.log('Submitting Work Order:', { status, description });
  };

  // A filter change invalidates the current offset: page 4 of the old result set is usually empty
  // in the new one.
  const handleCompanyChange = (filters: MaintenancesFilters) => {
    setMaintenancesFilters(filters);
    setCompanyName(filters.company_name ?? undefined);
    if (filters.time_range) {
      setTimeRangeFilter(filters.time_range);
    }
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const onClearDateRangeFilter = () => {
    setMaintenancesFilters((prevFilters) => ({
      ...prevFilters,
      time_range: '7d',
      start_time: undefined,
      end_time: undefined,
    }));
    setTimeRangeFilter('7d');
    setDates(null);
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const onChangeDateRangeFilter = (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => {
    setMaintenancesFilters((prevFilters) => ({
      ...prevFilters,
      time_range: undefined,
      start_time: filterValue.time_range.start_time,
      end_time: filterValue.time_range.end_time,
    }));
    setTimeRangeFilter(
      undefined,
      filterValue.time_range.start_time,
      filterValue.time_range.end_time
    );
    onPaginationChanged(DEFAULT_PAGINATION.page, paginationDetails.items_per_page);
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  return (
    <Flex vertical className={styles.container}>
      <CustomBreadcrumb items={items} />

      <div>
        <MaintenancesHeader
          onSearchQueryChange={setCompanySearchQuery}
          searchResults={companySearchData}
          isSearchLoading={companySearchLoading}
          searchError={companySearchError}
          workOrders={maintenancesData?.maintenances_count || 0}
          onRefreshData={handleRefreshData}
          filters={maintenancesFilters}
          onChangeFilters={handleCompanyChange}
          companiesList={companies?.map((company) => company.name) || []}
          dates={dates}
          setDates={setDates}
          onDateRangeClear={onClearDateRangeFilter}
          onChangeDateRangeFilter={onChangeDateRangeFilter}
        />

        <MaintenanceTable
          currentPage={paginationDetails.page}
          onShowMaintenanceDetails={handleShowMaintenanceDetails}
          onChange={handleChange}
          maintenancesData={maintenancesData?.maintenances}
          isAdmin={isAdmin}
          pagination={maintenancesData?.pagination}
          loading={isPending}
        />

        <WorkOrderDetailsDrawer
          workOrdersFields={filteredData}
          isVisible={isMaintenanceDrawerVisible}
          onClose={closeDrawer}
          onSubmit={handleSubmit}
        />
      </div>
    </Flex>
  );
}
